import {
  ChangeDetectorRef,
  Component,
  Input,
  OnDestroy,
  OnInit,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import {
  GeneralFilter,
  KeyValueGroup,
  Location,
  Partner,
  Project,
  Service,
  ConfirmationModalComponent,
  ConfirmationModalConfig,
} from '@app/shared';
import { ProjectUtilsService } from '../../services/project-utils.service';
import {
  CommunicationService,
  DocumentService,
  ProjectService,
  ScreenService,
} from '@app/core';
import { GlobalValuesService } from '../../../../core/services/global-values.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { forkJoin, Subject, takeUntil } from 'rxjs';
import { AuthService } from '../../../../core/services/auth.service';
import { environment } from '../../../../../environments/environment';

@Component({
  selector: 'app-project-detail',
  standalone: false,

  templateUrl: './project-detail.component.html',
  styleUrl: './project-detail.component.less',
})
export class ProjectDetailComponent implements OnInit, OnDestroy {
  // Exponer las referencias a los templates mediante ViewChild
  @ViewChild('mainTpl', { static: true }) mainTemplate!: TemplateRef<any>;
  @ViewChild('sideTpl', { static: true }) sideTemplate!: TemplateRef<any>;
  @ViewChild('confirmationModal')
  confirmationModal!: ConfirmationModalComponent;
  private pendingState?: string;

  project!: Project;

  partnerOptions: Partner[] = [];
  cecEmployeeList: Partner[] = [];
  clientsList: Partner[] = [];

  selectedTabIndex = 0;

  isMobile$;

  loading: boolean = true;
  projectNotFound: boolean = false;

  filters: GeneralFilter[] = [
    { name: 'Todos', icon: 'all_inclusive', active: true, code: 'All' },
    {
      name: 'No validado',
      icon: 'close',
      active: false,
      code: 'NonValidated',
      class: 'subtle-red',
    },
    {
      name: 'Validado',
      icon: 'verified',
      active: false,
      code: 'Validated',
      class: 'subtle-purple',
    },
    {
      name: 'Planificación',
      icon: 'event_upcoming',
      active: false,
      code: 'Planning',
      class: 'subtle-pink',
    },
    {
      name: 'En ejecución',
      icon: 'motion_play',
      active: false,
      code: 'InExecution',
      class: 'subtle-blue',
    },
    {
      name: 'Finalizado',
      icon: 'check_circle',
      active: false,
      code: 'Finalized',
      class: 'subtle-teal',
    },
  ];

  private destroy$ = new Subject<void>();

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private ps: ProjectUtilsService,
    private screenService: ScreenService,
    private projectService: ProjectService,
    private communicationService: CommunicationService,
    private documentService: DocumentService,
    public globalValuesService: GlobalValuesService,
    public authService: AuthService
  ) {
    this.isMobile$ = screenService.isMobile$;
    this.globalValuesService.isPartnerReady$
      .pipe(takeUntilDestroyed())
      .subscribe((value) => {
        if (value) {
        }
        this.partnerOptions = globalValuesService.partnerlist;
        this.cecEmployeeList = globalValuesService.cecEmployeeList;
        this.clientsList = globalValuesService.clientsList;
      });
    this.globalValuesService.places$
      .pipe(takeUntilDestroyed())
      .subscribe((value) => (this.locations = value));
  }

  ngOnInit(): void {
    this.route.queryParams
      .pipe(takeUntil(this.destroy$))
      .subscribe((params) => {
        if (params['openDocuments'] === 'true') {
          this.selectedTabIndex = 1;
          //remover parámetro después de usarlo
          this.router.navigate([], {
            queryParams: { openChat: null },
            queryParamsHandling: 'merge',
          });
        }
      });
    this.ps
      .getProjectAndServiceData()
      .pipe(takeUntil(this.destroy$))
      .subscribe((value) => {
        if (value) {
          this.project = this.ps.selectedProject!;
          this.loading = false;
          // Inicializar servicios filtrados
          this.filter('All');
          if (!this.project.chat) {
            this.getDocumentsAndChat();
          } else {
            this.ps.currentDetailChat$.next(this.project.chat);
            this.ps.currentChatEntetyName$.next(this.project.name);
          }
        } else {
          this.loading = false;
          this.projectNotFound = true;
        }
      });
  }

  getDocumentsAndChat() {
    const request = {
      chat: this.communicationService.getChat({
        startRow: 0,
        endRow: 1,
        sortProperty: 'id',
        sortDirection: 'asc',
        filterProperty: 'id',
        filterValue: this.project.chatId.toString(),
      }),
      documents: this.documentService.getDocument({
        startRow: 0,
        endRow: 10000,
        sortDirection: 'asc',
        sortProperty: 'id',
        entityId: this.project.id,
        entityType: 'Project',
      }),
    };

    forkJoin(request)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: ({ chat, documents }) => {
          // Transformación equivalente al map
          this.project = {
            ...this.project,
            documents: documents?.data?.rows || [],
            chat:
              chat?.data?.rows && chat.data.rows.length > 0
                ? chat.data.rows[0]
                : undefined,
          };

          this.ps.selectedProject = this.project;
          this.ps.currentDetailChat$.next(this.project.chat);
          this.ps.currentChatEntetyName$.next(this.project.name);
        },
        error: (e) => console.error(e),
      });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  openStateChangeConfirmation(value: string): void {
    this.pendingState = value;
    const config: ConfirmationModalConfig = {
      title: 'Cambiar estado del proyecto',
      message: '¿Estás seguro que deseas cambiar el estado del proyecto?',
      confirmText: 'Cambiar',
      cancelText: 'Cancelar',
      confirmButtonType: 'primary',
      confirmButtonDanger: false,
    };
    this.confirmationModal.config = config;
    this.confirmationModal.open();
  }

  onStateChangeConfirmed(data: { textValue?: string; dateValue?: Date }): void {
    // Actualizar el estado del modal para mostrar loading
    this.confirmationModal.config.processing = true;
    if (this.pendingState) {
      this.setState(this.pendingState);
    }
  }

  filteredServices: Service[] = [];
  locations: Location[] = [];

  goToProjects(): void {
    this.router.navigate(['/projects']).then();
  }

  filter(filterCode?: string, searchString?: string): void {
    // Actualizar el estado activo de los filtros
    if (filterCode) {
      this.filters.forEach((f) => {
        f.active = f.code === filterCode;
      });
    }

    // Filtrar servicios por status
    if (!this.project || !this.project.services) {
      this.filteredServices = [];
      return;
    }

    let tempFilter = [...this.project.services];

    // Aplicar filtro de status
    if (filterCode && filterCode !== 'All') {
      tempFilter = tempFilter.filter((service) => {
        // Obtener el estado del servicio
        const serviceState = this.getServiceState(service);
        return serviceState === filterCode;
      });
    }

    // Aplicar búsqueda si existe
    if (searchString !== undefined && searchString.trim() !== '') {
      const searchLower = searchString.toLowerCase();
      tempFilter = tempFilter.filter(
        (s) =>
          s.name?.toLowerCase().includes(searchLower) ||
          s.description?.toLowerCase().includes(searchLower)
      );
    }

    this.filteredServices = tempFilter;
  }

  getServiceState(service: Service): string | undefined {
    // Si el servicio tiene un campo state, usarlo
    if ((service as any).state) {
      return (service as any).state;
    }
    // Si no, usar el estado del primer serviceOrder si existe
    if (
      service.serviceOrders &&
      service.serviceOrders.length > 0 &&
      service.serviceOrders[0]
    ) {
      return service.serviceOrders[0].state?.toString();
    }
    // Si no hay serviceOrders, retornar undefined
    return undefined;
  }

  getLocation(id: number | undefined): Location | undefined {
    return this.locations.find((l) => l.id === id);
  }

  navigateToService(service: Service): void {
    this.router
      .navigate(['/projects', 'detail', this.project.id, 'service', service.id])
      .then();
  }

  setState(value: string): void {
    this.projectService
      .patchProject({ id: this.project.id, status: value })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.project.status = value;
          this.confirmationModal.config.processing = false;
          this.confirmationModal.forceClose();
        },
        error: (e) => {
          console.error(e);
          this.confirmationModal.config.processing = false;
          this.confirmationModal.forceClose();
        },
      });
  }

  setKeyValueData(): KeyValueGroup[] {
    return [
      {
        layout: 'normal',
        name: 'normal',
        items: [
          {
            parentObject: this.project,
            component: 'ProjectDetailComponent',
            label: 'Ref. proyecto',
            noWrap: false,
            editable: !!this.authService.loggedUser$.value?.editProject,
            required: false,
            type: 'text',
            value: this.project!.reference,
            path: 'reference',
          },
          {
            label: 'Presupuesto',
            parentObject: this.project,
            component: 'ProjectDetailComponent',
            editable: false,
            noWrap: false,
            required: false,
            type: 'btn',
            path: this.project.odooEntityId
              ? 'odooEntityId'
              : 'odooSaleOrderLineId',
            value: {
              label: this.project.odooEntityId
                ? this.project.odooEntityId.toString()
                : this.project?.odooSaleOrderLineId?.toString(),
              localPrefix: 'odoo-icon.svg',
              sufix: 'open_in_new',
              link:
                environment.odooBaseUrl +
                (this.project.odooEntityId?.toString() ||
                  this.project?.odooSaleOrderLineId?.toString() ||
                  this.project?.odooSaleOrderOptionId?.toString()),
              isOdoo: true,
            },
          },
          {
            label: 'Drive',
            parentObject: this.project,
            component: 'ProjectDetailComponent',
            editable: true,
            noWrap: false,
            required: false,
            type: 'btn',
            path: 'driveLink',
            value: {
              label: this.project.driveLink,
              localPrefix: 'google_drive-icon.svg',
              sufix: 'open_in_new',
              link: this.project.driveLink,
              isRawLink: true,
            },
          },
          {
            label: 'Cliente',
            parentObject: this.project,
            component: 'ProjectDetailComponent',
            list: this.clientsList,
            noWrap: false,
            editable: !!this.authService.loggedUser$.value?.editProject,
            required: false,
            type: 'avatar',
            path: 'partnerId',
            value: {
              userId: this.project!.partnerId,
              alt: 'Cliente',
              centered: false,
            },
          },
          {
            label: 'Ejecutivo Operaciones',
            parentObject: this.project,
            component: 'ProjectDetailComponent',
            list: this.cecEmployeeList,
            noWrap: false,
            editable: !!this.authService.loggedUser$.value?.editProject,
            required: false,
            type: 'avatar',
            path: 'operationExecutiveId',
            value: {
              userId: this.project!.operationExecutiveId,
              alt: 'Cliente',
              centered: false,
            },
          },
          {
            label: 'Gerente Administración',
            parentObject: this.project,
            component: 'ProjectDetailComponent',
            list: this.cecEmployeeList,
            noWrap: false,
            editable: !!this.authService.loggedUser$.value?.editProject,
            required: false,
            type: 'avatar',
            path: 'deputyProjectManagerId',
            value: {
              userId: this.project!.deputyProjectManagerId,
              alt: 'Cliente',
              centered: false,
            },
          },
          // {
          //   label: 'Subgerente Proyectos',
          //   parentObject: this.project,
          //   component: 'ProjectDetailComponent',
          //   list: this.cecEmployeeList,
          //   noWrap: false,
          //   editable: !!this.authService.loggedUser$.value?.editProject,
          //   required: false,
          //   type: 'avatar',
          //   path: 'administrationAgentId',
          //   value: {
          //     userId: this.project!.administrationAgentId,
          //     alt: 'Cliente',
          //     centered: false
          //   },
          // },
          {
            label: 'Ejecutivo Comercial',
            parentObject: this.project,
            component: 'ProjectDetailComponent',
            list: this.cecEmployeeList,
            noWrap: false,
            editable: !!this.authService.loggedUser$.value?.editProject,
            required: false,
            type: 'avatar',
            path: 'commercialExecutiveId',
            value: {
              userId: this.project!.commercialExecutiveId,
              alt: 'commercialExecutive',
              centered: false,
            },
          },
          {
            label: 'Jefe de Operaciones',
            parentObject: this.project,
            component: 'ProjectDetailComponent',
            list: this.cecEmployeeList,
            noWrap: false,
            editable: !!this.authService.loggedUser$.value?.editProject,
            required: false,
            type: 'avatar',
            path: 'operationsManagerId',
            value: {
              userId: this.project!.operationsManagerId,
              alt: 'gerenteOperaciones',
              centered: false,
            },
          },
          {
            label: 'Fecha inicio',
            parentObject: this.project,
            component: 'ProjectDetailComponent',
            noWrap: false,
            editable: !!this.authService.loggedUser$.value?.editProject,
            required: false,
            type: 'date',
            path: 'startPlanDate',
            value: {
              startDate: this.project!.startPlanDate,
            },
          },
          {
            label: 'Fecha fin',
            parentObject: this.project,
            component: 'ProjectDetailComponent',
            noWrap: false,
            editable: !!this.authService.loggedUser$.value?.editProject,
            required: false,
            type: 'date',
            path: 'endPlanDate',
            value: {
              startDate: this.project!.endPlanDate,
            },
          },
        ],
      },
    ];
  }

  cancelProjectChanges(): void {
    // Recargar los datos del proyecto para deshacer cambios
    this.loading = true;
    this.ps
      .getProjectAndServiceData()
      .pipe(takeUntil(this.destroy$))
      .subscribe((value) => {
        if (value) {
          this.project = this.ps.selectedProject!;
          this.loading = false;
          // Re-inicializar servicios filtrados
          this.filter('All');
        }
      });
  }

  saveProjectChanges(): void {
    // Aquí iría la lógica para guardar los cambios del proyecto
    // Por ahora solo mostramos un mensaje
    console.log('Guardando cambios del proyecto:', this.project);
    // TODO: Implementar el guardado real del proyecto
  }
}
